/**
 * EdgeBase — the shared renderer for both DependencyEdge and InheritanceEdge
 * (phase 6b, step 2).
 *
 * The EDG edges are FLOATING edges drawn from precomputed geometry: the path `d`,
 * the marker ids, the highlight class and the label list are all derived UPSTREAM
 * in `EdgFlow.deriveEdges` (where the uimodel node rects are in scope) and handed
 * to the edge through `data`. This keeps the geometry pixel-identical to the old
 * Rappid paper (see `edgeGeometry.ts`) and independent of React Flow's async node
 * measurement — exactly what a render-only parity stage needs.
 *
 * The component itself only paints:
 *   - a `<path>` with the precomputed `d`, `markerStart` / `markerEnd` string refs
 *     to the `merode-marker-*` ids (markers.tsx), stroke black 1px (the old
 *     `.connection` stroke from `createEDGLink`), and the highlight class on the
 *     wrapping `<g>` (CSS in edgFlow.scss recolours stroke + markers via the
 *     variant marker ids chosen upstream).
 *   - the labels as `EdgeLabelRenderer` divs positioned at the precomputed
 *     screen-space points (white rounded background, Arial 14px, centred — the
 *     JointJS default link label style).
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { EdgeLabelRenderer, type EdgeProps, type Edge } from '@xyflow/react';
import type { Pt } from './edgeGeometry';
import type { HighlightClass } from './ObjectTypeNode';
import VertexHandles, { type VertexDragLive } from './VertexHandles';
import { vertexHandlesVisible } from './vertexEditing';

export type EdgeLabel = {
  /** Stable key for React. */
  key: string;
  /** The text to show. */
  text: string;
  /** Position of the label centre in FLOW (graph) coordinates. */
  point: Pt;
};

export type EdgeFlowData = {
  /** Precomputed SVG path `d` (rounded connector through boundary points). */
  d: string;
  /** marker-start id (no `url(#...)` wrapper), or null for no marker. */
  markerStartId: string | null;
  /** marker-end id, or null for no marker. */
  markerEndId: string | null;
  /** Highlight class for stroke/label recolour, or null. */
  highlight: HighlightClass;
  /** Labels to render (UML cardinalities / peer display name). */
  labels: EdgeLabel[];
  /** Source point + vertices + target point for segment-aware vertex insertion. */
  points?: Pt[];
  /** Which uimodel link store backs this edge — routes vertex commits (6c). */
  kind?: 'dependency' | 'inheritance';
  /**
   * The persisted vertices (or, during a live vertex drag, the transient ones).
   * Drives the draggable vertex handles when the edge is selected. Present only
   * in interactive mode.
   */
  vertices?: Pt[];
  /** Commit a replaced vertices array (existing updateEDG*Vertices command). */
  onChangeVertices?: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    vertices: Pt[]
  ) => void;
  /** Report live vertices during a handle drag so the path follows (6c). */
  onVertexDragLive?: VertexDragLive;
  /** Selected bend index for keyboard deletion / visual state. */
  selectedVertexIndex?: number | null;
  /** Select a bend handle without changing the selected edge. */
  onSelectVertex?: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    index: number
  ) => void;
  /** Delete a bend handle through the same flow-level vertex command. */
  onDeleteVertex?: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    index: number
  ) => void;
  [key: string]: unknown;
};

export type EdgeFlowEdge = Edge<EdgeFlowData>;

const LABEL_FONT_FAMILY = 'Arial, helvetica, sans-serif';

/**
 * Render a precomputed floating edge. Used by both edge types — the only
 * difference between a dependency and an inheritance is the marker ids and labels
 * chosen upstream, so the renderer is shared.
 */
export default function EdgeBase({ id, data, selected }: EdgeProps<EdgeFlowEdge>) {
  if (!data) return null;
  const {
    d,
    markerStartId,
    markerEndId,
    highlight,
    labels,
    kind,
    vertices,
    onChangeVertices,
    onVertexDragLive,
    selectedVertexIndex,
    onSelectVertex,
    onDeleteVertex,
  } = data;

  const groupClass = ['merode-edge', highlight].filter(Boolean).join(' ');

  // Vertex-editing handles: only on the SELECTED edge in interactive mode (the
  // handlers + vertices are present only then). Mirrors the Rappid LinkView whose
  // vertex handles appeared for the active link. Inserting a vertex also selects
  // the edge so a new knikpunt is visible even if the edge was not selected.
  const showVertexHandles =
    vertices != null &&
    vertexHandlesVisible({ selected: selected === true, vertices }) &&
    onChangeVertices != null &&
    onVertexDragLive != null;

  return (
    <>
      {/*
        React Flow renders the edge component INSIDE the edges <svg>, so a bare
        <g>/<path> is valid SVG here. The highlight class on the <g> lets the CSS
        recolour the stroke (markers are recoloured by swapping the variant id
        upstream, matching the old baked-colour markers).
      */}
      <g className={groupClass}>
        {/*
          Invisible wide hit-area — port of Rappid's `.connection-wrap` (15px
          stroke in the default theme) so clicking/hovering an edge doesn't
          require landing on the 1px visible line. `pointerEvents: 'stroke'`
          catches hits on the transparent stroke; the events bubble to React
          Flow's edge group, driving onEdgeClick / onEdgeDoubleClick /
          onEdgeMouseEnter exactly like the visible path.
        */}
        <path
          className="merode-edge-wrap"
          d={d}
          fill="none"
          stroke="transparent"
          strokeWidth={15}
          strokeLinecap="round"
          style={{ pointerEvents: 'stroke' }}
        />
        <path
          className="merode-edge-path"
          d={d}
          fill="none"
          stroke="black"
          strokeWidth={1}
          markerStart={markerStartId ? `url(#${markerStartId})` : undefined}
          markerEnd={markerEndId ? `url(#${markerEndId})` : undefined}
        />
      </g>
      <EdgeLabelRenderer>
        {labels.map((label) => (
          <div
            key={`${label.key}:${label.text}`}
            className={['merode-edge-label', highlight].filter(Boolean).join(' ')}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${label.point.x}px, ${label.point.y}px)`,
              fontFamily: LABEL_FONT_FAMILY,
              fontSize: 14,
              lineHeight: 1,
              color: '#000000',
              background: '#ffffff',
              borderRadius: 3,
              padding: '0 1px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {label.text}
          </div>
        ))}
      </EdgeLabelRenderer>
      {showVertexHandles ? (
        <VertexHandles
          edgeId={id}
          kind={kind === 'inheritance' ? 'inheritance' : 'dependency'}
          vertices={vertices}
          onChangeVertices={onChangeVertices}
          onVertexDragLive={onVertexDragLive}
          selectedIndex={selectedVertexIndex ?? null}
          onSelectVertex={onSelectVertex}
          onDeleteVertex={onDeleteVertex}
        />
      ) : null}
    </>
  );
}
